"use client";

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";

import {
  cancelAutomationsPlan,
  changeAutomationsPlan,
  previewAutomationsChange,
  resumeAutomationsPlan,
} from "../../client/manager/automations";
import type {
  AutomationsEntitlementResponse,
  AutomationsUpgradePreview,
} from "../../models/automations-entitlement";
import type { AutomationsPlanKey } from "../../models/automations-plans";
import { invalidateAutomations } from "./automationsInvalidation";
import { automationsKeys } from "./automationsQueryKeys";

type EntitlementMutation<TVariables = void> = UseMutationResult<
  AutomationsEntitlementResponse,
  Error,
  TVariables
>;

export interface AutomationsPlanActions {
  change: EntitlementMutation<AutomationsPlanKey>;
  cancel: EntitlementMutation;
  resume: EntitlementMutation;
}

/** A mutation writes the returned entitlement into the cache, so the card updates without waiting out the read's staleTime. */
export function useAutomationsPlanActions(
  artistId: string
): AutomationsPlanActions {
  const queryClient = useQueryClient();
  const writeEntitlement = (entitlement: AutomationsEntitlementResponse) =>
    queryClient.setQueryData(
      automationsKeys.entitlement(artistId),
      entitlement
    );

  const change = useMutation({
    mutationFn: (target: AutomationsPlanKey) =>
      changeAutomationsPlan(artistId, target),
    onSuccess: (entitlement) => {
      writeEntitlement(entitlement);
      void invalidateAutomations(queryClient, {
        artistId,
        scope: ["billing", "creditBalance"],
      });
    },
  });
  const cancel = useMutation({
    mutationFn: () => cancelAutomationsPlan(artistId),
    onSuccess: writeEntitlement,
  });
  const resume = useMutation({
    mutationFn: () => resumeAutomationsPlan(artistId),
    onSuccess: writeEntitlement,
  });

  return { change, cancel, resume };
}

/** Today's prorated charge for a change that takes effect now; a null target runs no quote. Never cached, since the proration moves by the minute. */
export function useAutomationsChangePreview(
  artistId: string,
  target: AutomationsPlanKey | null
): UseQueryResult<AutomationsUpgradePreview> {
  return useQuery({
    queryKey: automationsKeys.changePreview(artistId, target),
    queryFn: () => previewAutomationsChange(artistId, target!),
    enabled: !!target,
    gcTime: 0,
    retry: false,
  });
}
